import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Box, Chip, LinearProgress, Stack, Typography } from '@mui/material';
import useRootHook from '../../hooks/useRoot';
import stats from '../../models/stats';
import transactions from '../../models/transactions';
import helpers from '../../helpers/common';

const STATUS_ORDER = [
  transactions.statusToEnum.completed,
  transactions.statusToEnum.hold,
  transactions.statusToEnum.declined,
  transactions.statusToEnum.refunded,
  transactions.statusToEnum.disputed,
  transactions.statusToEnum.blocked,
  transactions.statusToEnum.new,
  transactions.statusToEnum.expired,
];

const toTitle = (s) => {
  if (!s) return '';
  const str = String(s);
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export default function StatusBreakdownCard({ filters }) {
  const { defaultCurrency } = useRootHook();
  const [data, setData] = useState(null);

  useEffect(() => {
    stats.getStats({ ...filters, type: 'statusBreakdown' }).then((d) => {
      setData(d || null);
    });
  }, [filters]);

  const rows = useMemo(() => {
    const list = Array.isArray(data?.breakdown) ? data.breakdown : [];
    const map = new Map(list.map((r) => [String(r.status), r]));
    return STATUS_ORDER
      .filter((s) => map.has(String(s)))
      .map((s) => map.get(String(s)));
  }, [data]);

  const totalCount = rows.reduce((acc, r) => acc + (parseInt(r.count, 10) || 0), 0) || 1;
  const totalAmount = rows.reduce((acc, r) => {
    const amount = typeof r.amount === 'string' ? parseFloat(r.amount) : (r.amount || 0);
    return acc + amount;
  }, 0);
  const totalAmountStr = defaultCurrency
    ? helpers.getSubunitAmountString(totalAmount, defaultCurrency.symbol, defaultCurrency.subunit)
    : String(totalAmount);

  return (
    <Stack spacing={1.25}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          Payments breakdown
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'primary.main' }}>
          {totalAmountStr}
        </Typography>
      </Stack>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No data
        </Typography>
      ) : (
        rows.map((r) => {
          const count = parseInt(r.count, 10) || 0;
          const pct = Math.round((count / totalCount) * 100);
          const statusName = transactions.enumToStatus[String(r.status)] || String(r.status);
          const amount = typeof r.amount === 'string' ? parseFloat(r.amount) : (r.amount || 0);
          const amountStr = defaultCurrency
            ? helpers.getSubunitAmountString(amount, defaultCurrency.symbol, defaultCurrency.subunit)
            : String(amount);

          return (
            <Box key={`st-${r.status}`}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 0.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip size="small" label={toTitle(statusName)} variant="outlined" />
                  <Typography variant="body2" color="text.secondary">
                    {count} ({pct}%)
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {amountStr}
                </Typography>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  height: 6,
                  borderRadius: 999,
                  bgcolor: 'rgba(10, 37, 64, 0.06)',
                  '& .MuiLinearProgress-bar': { borderRadius: 999 },
                }}
              />
            </Box>
          );
        })
      )}
    </Stack>
  );
}



